import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronRight, X } from "lucide-react";

const STEPS = [
  {
    title: "EAT.",
    color: "#FF6A00",
    body: "Feed Resmo your resume once. PDF, DOCX, or paste — Resmo remembers it forever.",
  },
  {
    title: "DIGEST.",
    color: "#20C1A7",
    body: "Drop any job description (paste, URL, or file). Resmo crunches the company, the role's real problem, and your fit.",
  },
  {
    title: "IMPRESS.",
    color: "#FFC145",
    body: "Get a tailored resume, fit matrix, interview prep, cover letter, and salary range — all in one place.",
  },
];

export default function OnboardingTour() {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const location = useLocation();

  // Restrict tour to safe surfaces only — never overlay deep tool pages
  const TOUR_PATHS = ["/", "/feed-resume", "/feed-job", "/dashboard"];
  const onTourPath = TOUR_PATHS.some((p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p)
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/account/preferences");
        if (mounted && !data.onboarding_done) setShow(true);
      } catch {
        /* unauthenticated — skip */
      }
    })();
    return () => { mounted = false; };
  }, []);

  const finish = async (skipped = false) => {
    setShow(false);
    try {
      await api.patch("/account/preferences", { onboarding_done: true });
    } catch {
      /* ignore */
    }
  };

  if (!show || !onTourPath) return null;

  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(15, 27, 45, 0.65)" }}
      data-testid="onboarding-overlay"
    >
      <div
        className="rm-card-static p-6 md:p-10 w-full max-w-lg relative"
        style={{ background: "#FFF2EC" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => finish(true)}
          className="absolute top-3 right-3 rm-btn rm-btn-ghost text-xs px-2 py-2"
          data-testid="onboarding-close"
          aria-label="Close"
        >
          <X size={14} />
        </button>

        <div className="rm-tag mb-3">Welcome to Resmo</div>
        <h2 className="font-display text-4xl uppercase leading-tight">
          <span style={{ color: s.color }}>{s.title}</span>
        </h2>
        <p className="text-base mt-3" style={{ color: "#0F1B2D" }} data-testid="onboarding-body">
          {s.body}
        </p>

        <div className="flex justify-center gap-2 mt-6" data-testid="onboarding-dots">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-full"
              style={{
                background: i === step ? "#FF6A00" : "#CBD5E1",
                border: "2px solid #0F1B2D",
              }}
            />
          ))}
        </div>

        <div className="flex justify-between items-center mt-6 gap-3 flex-wrap">
          <button
            onClick={() => finish(true)}
            className="font-mono-rm text-xs uppercase tracking-widest underline"
            style={{ color: "#475569" }}
            data-testid="onboarding-skip"
          >
            Skip tour
          </button>
          <button
            onClick={() => last ? finish() : setStep(step + 1)}
            className="rm-btn rm-btn-primary"
            data-testid="onboarding-next"
          >
            {last ? "Let's go" : "Next"} <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
